import React from "react";
import { useLang } from "./LanguageProvider";
import { Check, X } from "lucide-react";

export default function Comparison() {
  const { t, isRtl } = useLang();
  return (
    <section
      id="compare"
      data-testid="compare-section"
      className="relative bg-brand-navy py-24 md:py-40 border-t border-white/5"
    >
      <div className="container mx-auto px-6 md:px-10">
        <div className="max-w-3xl reveal">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-brand-teal" />
            <span className="text-[11px] uppercase tracking-[0.28em] text-brand-teal font-semibold">
              {t.compare.overline}
            </span>
          </div>
          <h2 className="font-heading text-white text-3xl sm:text-4xl md:text-5xl lg:text-[56px] leading-[1.05] font-medium tracking-tight">
            {t.compare.title}
          </h2>
          <p className={`mt-6 text-white/65 text-base md:text-lg leading-relaxed ${isRtl ? "text-right" : "text-left"}`}>
            {t.compare.lead}
          </p>
        </div>

        <div className="reveal reveal-delay-1 mt-12 md:mt-16 rounded-2xl border border-white/10 overflow-hidden">
          <table className="compare-table w-full border-collapse md:table-fixed" data-testid="compare-table">
            <caption className="sr-only">
              {isRtl
                ? "مقارنة بين CPVC و PPR عبر مقاييس الأداء"
                : "Comparison of CPVC and PPR across performance metrics"}
            </caption>
            <colgroup>
              <col className="w-full md:w-5/12" />
              <col className="w-1/2 md:w-4/12" />
              <col className="w-1/2 md:w-3/12" />
            </colgroup>
            <thead>
              <tr className="bg-brand-surface border-b border-white/10">
                <th
                  scope="col"
                  className="hidden md:table-cell text-left px-6 py-5 text-[11px] uppercase tracking-[0.22em] text-white/45 font-semibold"
                >
                  {isRtl ? "المقياس" : "metric"}
                </th>
                <th
                  scope="col"
                  className="text-left px-6 py-5 border-l border-white/10 align-middle"
                >
                  <span className="inline-flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-brand-teal" />
                    <span className="text-sm text-white font-semibold">{t.compare.cpvc_label}</span>
                  </span>
                </th>
                <th
                  scope="col"
                  className="text-left px-6 py-5 border-l border-white/10 align-middle"
                >
                  <span className="inline-flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-white/30" />
                    <span className="text-sm text-white/60 font-medium">{t.compare.ppr_label}</span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {t.compare.rows.map((r, i) => (
                <tr
                  key={i}
                  data-testid={`compare-row-${i}`}
                  className={`${
                    i !== t.compare.rows.length - 1 ? "border-b border-white/5" : ""
                  } hover:bg-white/[0.02] transition-colors`}
                >
                  <th
                    scope="row"
                    className="block md:table-cell text-left px-6 pt-5 pb-2 md:py-6 text-white/85 text-sm md:text-base font-normal align-top"
                  >
                    {r.metric}
                  </th>
                  <td className="px-6 py-6 md:border-l border-white/10 align-top">
                    <span className="flex items-start gap-3">
                      <span className="mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-teal/15 text-brand-teal flex-shrink-0">
                        <Check size={12} strokeWidth={2.5} aria-hidden="true" />
                      </span>
                      <span className="text-white text-sm md:text-base leading-snug">{r.cpvc}</span>
                    </span>
                  </td>
                  <td className="px-6 py-6 border-l border-white/10 align-top">
                    <span className="flex items-start gap-3">
                      <span className="mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/5 text-white/30 flex-shrink-0">
                        <X size={12} strokeWidth={2} aria-hidden="true" />
                      </span>
                      <span className="text-white/55 text-sm md:text-base leading-snug">{r.ppr}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className={`reveal reveal-delay-2 mt-6 text-xs text-white/35 ${isRtl ? "text-right" : "text-left"}`}>
          {t.compare.footnote}
        </p>
      </div>
      <style>{`
        @media (max-width: 767px) {
          .compare-table, .compare-table thead, .compare-table tbody, .compare-table tr { display: block; }
          .compare-table thead { background: #0D1E36; }
          .compare-table thead tr { display: grid; grid-template-columns: 1fr 1fr; }
          .compare-table tbody tr { display: grid; grid-template-columns: 1fr 1fr; }
          .compare-table th[scope="row"] { grid-column: 1 / -1; padding-bottom: 0; }
          .compare-table td { border-left: 0 !important; padding-top: 0.75rem; }
          .compare-table tbody tr td:first-of-type { border-right: 1px solid rgba(255,255,255,0.06); padding-right: 1rem; }
          .compare-table thead th:first-child { display: none; }
        }
      `}</style>
    </section>
  );
}
